import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Table,
  Space,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  message,
  Tag,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { usersService, type ApiUser } from '../../services/usersService';
import type { ColumnsType } from 'antd/es/table';
import UserAvatar from '../../components/Common/UserAvatar';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCSV } from '../../utils/exportUtils';

const { Option } = Select;

// Type pour afficher les utilisateurs dans le Table
interface UserRow {
  id: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  createdAt: string;
  profileImage?: string | null;
}

interface UserFilters {
  page: number;
  limit: number;
  search: string;
  role: string;
  status: string;
}

const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 10,
    search: '',
    role: '',
    status: '',
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await usersService.getUsers(filters);
      setUsers(response.data.users);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      message.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRoleFilter = (value: string) => {
    setRoleFilter(value);
    setFilters(prev => ({ ...prev, role: value || '', page: 1 }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value);
    setFilters(prev => ({ ...prev, status: value || '', page: 1 }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setFilters({
      page: 1,
      limit: 10,
      search: '',
      role: '',
      status: '',
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAddUser = () => {
    navigate('/users/new');
  };

  const handleEditUser = (id: number) => {
    navigate(`/users/edit/${id}`);
  };

  const handleViewUser = (id: number) => {
    navigate(`/users/${id}`);
  };

  const [exporting, setExporting] = useState<boolean>(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      // Récupérer l'ensemble des utilisateurs selon les filtres actifs (jusqu'à 5000)
      const res = await usersService.getUsers({
        ...filters,
        page: 1,
        limit: 5000,
      });
      const dataToExport = res?.data?.users || users;
      if (dataToExport.length === 0) {
        message.warning('Aucun utilisateur à exporter');
        return;
      }

      const roleLabels: Record<string, string> = {
        super_admin: 'Super Admin',
        admin: 'Administrateur',
        manager: 'Team Lead',
        agent: 'Agent',
        technician: 'Technicien',
        customer: 'Client',
      };

      const statusLabels: Record<string, string> = {
        active: 'Actif',
        pending: 'En attente',
        inactive: 'Inactif',
      };

      exportToCSV(
        'utilisateurs',
        [
          { header: 'ID', key: 'id' },
          { header: 'Prénom', key: 'first_name', formatter: (_, u) => u.first_name || '' },
          { header: 'Nom', key: 'last_name', formatter: (_, u) => u.last_name || '' },
          { header: 'Email', key: 'email', formatter: (val) => val || '' },
          { header: 'Téléphone', key: 'phone', formatter: (_, u) => u.phone || u.phoneNumber || '' },
          { header: 'Rôle', key: 'role', formatter: (val) => roleLabels[val] || val },
          { header: 'Statut', key: 'status', formatter: (val) => statusLabels[val] || val },
          { header: 'Date de création', key: 'createdAt', formatter: (val) => val ? new Date(val).toLocaleDateString('fr-FR') : '' },
        ],
        dataToExport
      );
      message.success(`${dataToExport.length} utilisateurs exportés avec succès`);
    } catch (err: any) {
      console.error('Erreur export utilisateurs:', err);
      message.error('Erreur lors de l\'export des utilisateurs');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      await usersService.deleteUser(id);
      message.success('Utilisateur supprimé avec succès');
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      message.error('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  // Conversion des données pour le tableau
  const rows: UserRow[] = users.map((user) => ({
    id: user.id,
    email: user.email,
    phone: user.phone || user.phoneNumber || '',
    firstName: user.firstName || user.first_name || '',
    lastName: user.lastName || user.last_name || '',
    role: user.role,
    status: user.status,
    createdAt: user.createdAt || new Date().toISOString(),
    profileImage: user.profile_image || null,
  }));

  // Configuration des colonnes
  const columns: ColumnsType<UserRow> = [
    {
      title: 'Avatar',
      key: 'avatar',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <UserAvatar
          src={record.profileImage}
          firstName={record.firstName}
          lastName={record.lastName}
          size={40}
        />
      ),
    },
    {
      title: 'Nom',
      key: 'name',
      render: (_, record) => (
        <Button 
          type="link" 
          onClick={() => handleViewUser(record.id)}
          style={{ padding: 0, fontWeight: 500 }}
        >
          {`${record.firstName} ${record.lastName}`.trim() || 'N/A'}
        </Button>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.phone && (
            <div style={{ fontWeight: 500 }}>
              📱 {record.phone}
            </div>
          )}
          {record.email && (
            <div style={{ color: '#666', fontSize: '12px' }}>
              ✉️ {record.email}
            </div>
          )}
          {!record.phone && !record.email && 'N/A'}
        </div>
      ),
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const roleConfig = {
          super_admin: { color: 'purple', text: 'Super Administrateur' },
          admin: { color: 'red', text: 'Administrateur' },
          manager: { color: 'orange', text: 'Team Lead' },
          agent: { color: 'geekblue', text: 'Agent' },
          technician: { color: 'blue', text: 'Technicien' },
          customer: { color: 'green', text: 'Client' },
        };
        const config = roleConfig[role as keyof typeof roleConfig] || { color: 'default', text: role };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          active: { color: 'green', text: 'Actif' },
          inactive: { color: 'red', text: 'Inactif' },
          pending: { color: 'orange', text: 'En attente' },
        };
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => new Date(text).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewUser(record.id)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEditUser(record.id)}
            />
          </Tooltip>
          {isAdmin && (
            <Popconfirm
              title="Supprimer cet utilisateur ?"
              onConfirm={() => handleDeleteUser(record.id)}
              okText="Oui"
              cancelText="Non"
              disabled={String(record.id) === String(currentUser?.id)}
            >
              <Tooltip title={String(record.id) === String(currentUser?.id) ? "Vous ne pouvez pas supprimer votre propre compte" : "Supprimer"}>
                <Button 
                  type="text" 
                  icon={<DeleteOutlined />} 
                  size="small"
                  danger
                  disabled={String(record.id) === String(currentUser?.id)}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Statistiques
  const stats = [
    { 
      title: 'Total Utilisateurs', 
      value: pagination.total,
      color: '#1890ff'
    },
    { 
      title: 'Actifs', 
      value: users.filter(u => u.status === 'active').length,
      color: '#52c41a'
    },
    { 
      title: 'Administrateurs', 
      value: users.filter(u => u.role === 'admin').length,
      color: '#f5222d'
    },
    { 
      title: 'Techniciens', 
      value: users.filter(u => u.role === 'technician').length,
      color: '#1890ff'
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* En-tête */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        backgroundColor: '#fff',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Gestion des Utilisateurs</h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Gérez les comptes utilisateurs et leurs permissions
          </p>
        </div>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchUsers}
            size="large"
          >
            Actualiser
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
            size="large"
            style={{ height: '40px' }}
          >
            Exporter
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            size="large"
            onClick={handleAddUser}
            style={{ height: '40px' }}
          >
            Nouvel Utilisateur
          </Button>
        </Space>
      </div>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card size="small" style={{ borderRadius: '8px' }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ 
                  color: stat.color,
                  fontSize: '24px',
                  fontWeight: 600
                }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '16px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Input
              placeholder="Rechercher par nom ou email..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filtrer par rôle"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={roleFilter || undefined}
              onChange={handleRoleFilter}
            >
              <Option value="super_admin">Super Administrateur</Option>
              <Option value="admin">Administrateur</Option>
              <Option value="manager">Team Lead</Option>
              <Option value="agent">Agent</Option>
              <Option value="technician">Technicien</Option>
              <Option value="customer">Client</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={statusFilter || undefined}
              onChange={handleStatusFilter}
            >
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
              <Option value="pending">En attente</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Button 
              onClick={handleResetFilters}
              size="large"
              style={{ width: '100%' }}
            >
              Réinitialiser
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} sur ${total} utilisateurs`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || 10,
              }));
              setFilters(prev => ({
                ...prev,
                page: page,
                limit: pageSize || 10,
              }));
            },
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default UsersList;